import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError, formatMoney } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Wallet, TrendingUp, TrendingDown, Pencil, Save, FileDown, MessageCircle, Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function EmployeeLedger() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingOpening, setEditingOpening] = useState(false);
  const [openingDraft, setOpeningDraft] = useState(0);
  const [savingOpening, setSavingOpening] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/employees/${id}/ledger`);
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const saveOpening = async () => {
    setSavingOpening(true);
    try {
      const e = data.employee;
      await api.patch(`/employees/${id}`, {
        name: e.name, phone: e.phone || "", role: e.role || "",
        joining_date: e.joining_date || "",
        wage_type: e.wage_type || "monthly",
        monthly_salary: Number(e.monthly_salary || 0),
        daily_wage: Number(e.daily_wage || 0),
        opening_balance: Number(openingDraft) || 0,
        active: e.active !== false,
        notes: e.notes || "",
      });
      toast.success("Opening balance updated");
      setEditingOpening(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingOpening(false); }
  };

  const fetchFile = async (path) => {
    const BASE = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("lpg_token") || "";
    const res = await fetch(`${BASE}/api${path}`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: "include",
    });
    if (!res.ok) throw new Error(res.statusText);
    return res.blob();
  };

  const downloadPdf = async () => {
    try {
      const blob = await fetchFile(`/employees/${id}/ledger/pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `salary_ledger_${(data?.employee?.name || id).replace(/[^A-Za-z0-9 _-]/g, "_").replace(/ +/g, "_")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("Failed to download PDF"); }
  };
  const downloadCsv = async () => {
    try {
      const blob = await fetchFile(`/exports/employee-ledger?employee_id=${id}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `salary_ledger_${(data?.employee?.name || id).replace(/[^A-Za-z0-9 _-]/g, "_").replace(/ +/g, "_")}.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error("Failed to download CSV"); }
  };

  const shareWhatsApp = () => {
    if (!data) return;
    const e = data.employee;
    const phoneRaw = (e.phone || "").replace(/\D/g, "");
    const phone = phoneRaw.length === 10 ? `91${phoneRaw}` : phoneRaw;
    const lines = [
      `Hi ${e.name},`,
      ``,
      `Your salary summary as of ${new Date().toLocaleDateString("en-IN")}:`,
      ``,
      `Opening Advance: ₹ ${formatMoney(data.opening_advance ?? data.opening_balance)}`,
      `Earned : ₹ ${formatMoney(data.total_earned)}`,
      `Paid   : ₹ ${formatMoney(data.total_paid)}`,
      `Pending: ₹ ${formatMoney(data.closing_balance)}`,
      ``,
      `For full ledger details, please contact us.`,
    ];
    const text = encodeURIComponent(lines.join("\n"));
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) return <div className="text-sm text-gray-500">Loading…</div>;
  if (!data) return <div>Not found</div>;

  const e = data.employee;
  const balPositive = data.closing_balance > 0;

  return (
    <div className="space-y-6" data-testid="employee-ledger-page">
      <Link to="/employees" className="text-sm font-semibold text-gray-500 hover:text-black inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Back to employees
      </Link>

      <div className="card-flat p-5 flex items-center justify-between flex-wrap gap-4">
        <div className="min-w-0">
          <div className="small-label text-gray-500">Employee</div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">{e.name}</h2>
          <div className="text-sm text-gray-600">
            {e.role || "—"} · {e.wage_type === "monthly" ? `₹ ${formatMoney(e.monthly_salary)} / month` : `₹ ${formatMoney(e.daily_wage)} / day`}
            {e.phone ? ` · ${e.phone}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
          <div>
            <div className="small-label text-gray-500">Pending Salary</div>
            <div className={`data-num text-3xl ${balPositive ? "text-[#B45309]" : data.closing_balance < 0 ? "text-[#15803D]" : "text-gray-500"}`}
              data-testid="emp-closing-balance">
              ₹ {formatMoney(data.closing_balance)}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {balPositive ? "we owe employee" : data.closing_balance < 0 ? "advance paid" : "all settled"}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={downloadPdf} className="btn-primary h-10 px-4 flex items-center gap-2 text-sm" data-testid="emp-pdf-btn">
              <FileDown size={15} /> PDF
            </button>
            <div className="flex gap-2">
              <button onClick={downloadCsv} className="h-9 px-3 border border-gray-300 rounded-sm font-semibold text-xs inline-flex items-center gap-1 hover:bg-gray-50" data-testid="emp-csv-btn">
                <Download size={13} /> CSV
              </button>
              <button onClick={shareWhatsApp} className="h-9 px-3 border border-[#15803D] text-[#15803D] rounded-sm font-semibold text-xs inline-flex items-center gap-1 hover:bg-[#15803D] hover:text-white" data-testid="emp-share-wa">
                <MessageCircle size={13} /> WhatsApp
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="small-label text-gray-500">Opening Advance</div>
            <Wallet size={18} className="text-gray-400" />
          </div>
          {!editingOpening ? (
            <>
              <div className="data-num text-2xl" data-testid="emp-opening-display">
                ₹ {formatMoney(data.opening_advance ?? data.opening_balance)}
              </div>
              <div className="text-xs text-gray-500 mt-1">Advance we paid (deducted from salary)</div>
              {isAdmin && (
                <button
                  onClick={() => { setOpeningDraft(data.opening_advance ?? data.opening_balance); setEditingOpening(true); }}
                  className="text-xs text-gray-500 hover:text-black inline-flex items-center gap-1 mt-2"
                  data-testid="edit-emp-opening-btn"
                >
                  <Pencil size={11} /> Edit advance
                </button>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2 mt-1">
              <input
                type="number" step="0.01"
                className="h-9 px-2 border border-gray-300 rounded-sm w-32 text-right data-num text-sm"
                value={openingDraft}
                onChange={(e) => setOpeningDraft(e.target.value)}
                autoFocus
                data-testid="emp-opening-input"
              />
              <button
                onClick={saveOpening}
                disabled={savingOpening}
                className="h-9 px-3 bg-black text-white rounded-sm text-xs font-bold inline-flex items-center gap-1"
                data-testid="save-emp-opening-btn"
              >
                <Save size={11} /> {savingOpening ? "…" : "Save"}
              </button>
              <button onClick={() => setEditingOpening(false)} className="h-9 px-2 text-xs text-gray-500 hover:text-black">Cancel</button>
            </div>
          )}
        </div>
        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="small-label text-gray-500">Earned (from attendance)</div>
            <TrendingUp size={18} className="text-[#15803D]" />
          </div>
          <div className="data-num text-2xl text-[#15803D]">₹ {formatMoney(data.total_earned)}</div>
        </div>
        <div className="card-flat p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="small-label text-gray-500">Paid (from expenses)</div>
            <TrendingDown size={18} className="text-[#B91C1C]" />
          </div>
          <div className="data-num text-2xl text-[#B91C1C]">₹ {formatMoney(data.total_paid)}</div>
        </div>
      </div>

      <div className="card-flat">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <div className="small-label text-gray-500">Salary Ledger · chronological</div>
          <Link to={`/employees/${id}/food-ledger`} className="small-label text-[#B45309] hover:underline inline-flex items-center gap-1" data-testid="link-food-ledger">
            View food ledger →
          </Link>
        </div>
        <div className="overflow-auto max-h-[600px]">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Description</th>
                <th className="num">Earned (Cr)</th>
                <th className="num">Paid (Dr)</th>
                <th className="num">Balance</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} data-testid={`emp-row-${i}`} className={r.kind === "opening" ? "bg-gray-50" : ""}>
                  <td className="data-num text-xs">{r.date || "—"}</td>
                  <td className="text-sm">{r.description}</td>
                  <td className="num text-[#15803D]">{r.credit ? formatMoney(r.credit) : ""}</td>
                  <td className="num text-[#B91C1C]">{r.debit ? formatMoney(r.debit) : ""}</td>
                  <td className="num font-bold">₹ {formatMoney(r.balance)}</td>
                </tr>
              ))}
              {data.rows.length <= 1 && (
                <tr><td colSpan="5" className="py-10 text-center text-gray-400 text-sm">
                  No salary activity yet. Record a salary expense from <Link className="underline font-semibold" to="/expenses">Expenses</Link> with category &quot;Salary&quot; to start tracking.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-gray-500">
        💡 <strong>Opening Advance</strong> = money we already paid the employee in advance (deducted from upcoming salary). <strong>Earned</strong> = auto-calculated from attendance records. <strong>Paid</strong> = expenses with category &quot;Salary&quot; tagged to this employee. Pending = Earned − Advance − Paid.
      </div>
    </div>
  );
}
